/**
 * Shared UI component for UI Filter Bar.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { normalizeDrawerLabel } from '@shared/utils';
import { UiFilterOption } from './ui-filter-bar.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

/**
 * COMPONENT: Ui Filter Bar
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-filter-bar',
  standalone: true,
  imports: [TranslatePipe, UiLabelTranslatePipe],
  templateUrl: './ui-filter-bar.component.html',
  styleUrl: './ui-filter-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiFilterBarComponent {
  readonly filters = input<UiFilterOption[]>([]);
  /** PARENT-CHILD CONTRACT: selected documents the input state boundary used by this file. */
  readonly selected = input('');
  readonly changed = output<string>();
  readonly normalizeLabel = normalizeDrawerLabel;

  /**
   * BUSINESS LOGIC: select
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  select(value: string, disabled?: boolean): void {
    if (disabled) {
      return;
    }

    this.changed.emit(value);
  }
}
